import { useMemo } from 'react';
import {
	ToothPathology,
	ToothRestorationMaterial,
	ToothRestorationQuality,
	ToothSurface,
} from '../../../../../graphql/graphql';
import { TOOTH_SURFACE_MATERIAL_COLORS } from '../../../Helpers/surfaces/toothSurfaceMaterialColors';
import separateDecayTypeSurfaces from '../../../Helpers/pathology/separateDecayTypeSurfaces';

const DEFAULT_BASE_COLOR = '#006bff';
const DEFAULT_UNCERTAIN_OUTLINE_COLOR = '#3f90ff';
const INSUFFICIENT_OUTLINE_COLOR = '#ff0000';

export const RESTORATION_OUTLINE_PROPS = {
	strokeWidth: 5,
	strokeDasharray: '10 5',
} as const;

interface Params {
	quality?: ToothRestorationQuality;
	material?: ToothRestorationMaterial;
	surface: ToothSurface;
	pathology?: ToothPathology;
}

export default function useRestorationSurfaceOutline({
	quality,
	material,
	surface,
	pathology,
}: Params) {
	const { secondaryDecaySurfaces } = separateDecayTypeSurfaces(pathology ?? {});

	const baseColor = material
		? TOOTH_SURFACE_MATERIAL_COLORS[material].base
		: DEFAULT_BASE_COLOR;

	const outlineColor = useMemo(() => {
		if (pathology && secondaryDecaySurfaces.includes(surface)) {
			return INSUFFICIENT_OUTLINE_COLOR;
		}

		switch (quality) {
			case ToothRestorationQuality.Insufficient:
				return INSUFFICIENT_OUTLINE_COLOR;
			case ToothRestorationQuality.Uncertain:
				return material
					? TOOTH_SURFACE_MATERIAL_COLORS[material].outline
					: DEFAULT_UNCERTAIN_OUTLINE_COLOR;
			default:
				return undefined;
		}
	}, [quality, material, pathology, secondaryDecaySurfaces, surface]);

	const outlineProps = outlineColor
		? { ...RESTORATION_OUTLINE_PROPS, stroke: outlineColor }
		: undefined;

	return { baseColor, outlineProps };
}
